// Equipment master data (IE01) + counter/measurement readings (IK11) — the third tab of the
// preventive setup screen. The cost-center field is deliberately prominent: an equipment
// WITHOUT one makes technical completion fail months later (SAP IW177), which is why the
// create response carries tecoReady and the UI warns when it is false.
import React, { useState } from 'react';
import { Search, Plus, Gauge } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { Card, Button, Row } from './ui';
import { searchEquipment, createEquipment, listMeasuringPoints, createMeasurementDoc } from '../api';

const input = (isDarkMode) =>
  `w-full rounded-xl px-3 py-2 ${isDarkMode ? 'bg-white/10 text-white placeholder-white/40' : 'bg-slate-100 text-slate-800 placeholder-slate-400'}`;

export default function EquipmentPanel({ me, user, capabilities }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const role = me.profile.id;
  const canMaintain = me.profile.functionRights.canManageMasterData
    && (!capabilities || capabilities.equipmentMaintain !== false);

  const [form, setForm] = useState({ description: '', costCenter: '', plannerGroup: '' });
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Readings: find equipment -> pick measuring point -> record value.
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState(null);
  const [points, setPoints] = useState(null);       // { equipmentId, items }
  const [reading, setReading] = useState({ point: '', value: '' });
  const [readingResult, setReadingResult] = useState(null);

  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';

  const findEquipment = async () => {
    setHits(null); setPoints(null); setReadingResult(null);
    try {
      const res = await searchEquipment(user, query, role);
      setHits((res.items || []).slice(0, 5));
    } catch (e) { setError(e.message); }
  };

  const openPoints = async (equipmentId) => {
    setPoints({ equipmentId, items: null });
    try {
      const res = await listMeasuringPoints(user, equipmentId, role);
      setPoints({ equipmentId, items: res.items || [] });
      if ((res.items || []).length) setReading((r) => ({ ...r, point: res.items[0].point }));
    } catch (e) { setError(e.message); setPoints(null); }
  };

  return (
    <div>
      {error && <Card className="mb-3"><p className={isDarkMode ? 'text-rose-300 text-sm' : 'text-rose-600 text-sm'}>{error}</p></Card>}

      {canMaintain && (
        <Card className="mb-3">
          <p className={`flex items-center gap-2 font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            <Plus size={18} /> {t('pm.equip.newTitle')}
          </p>
          <div className="space-y-2">
            <input className={input(isDarkMode)} placeholder={t('pm.equip.description')} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="flex gap-2">
              <input className={input(isDarkMode)} placeholder={t('pm.equip.costCenter')} value={form.costCenter}
                onChange={(e) => setForm({ ...form, costCenter: e.target.value })} />
              <input className={input(isDarkMode)} placeholder={t('pm.equip.plannerGroup')} value={form.plannerGroup}
                onChange={(e) => setForm({ ...form, plannerGroup: e.target.value })} />
            </div>
            <p className={`text-xs ${sub}`}>{t('pm.equip.costCenterHint')}</p>
            <Button variant="confirm" className="!min-h-[48px]" disabled={busy || !form.description}
              onClick={async () => {
                setBusy(true); setError(null); setCreated(null);
                try {
                  const res = await createEquipment(user, {
                    description: form.description,
                    plant: me.activePlant,
                    ...(form.costCenter ? { costCenter: form.costCenter } : {}),
                    ...(form.plannerGroup ? { plannerGroup: form.plannerGroup } : {}),
                  }, crypto.randomUUID(), role);
                  if (res && res.ok === false) setError(res.error || t('pm.equip.createFailed'));
                  else { setCreated(res); setForm({ description: '', costCenter: '', plannerGroup: '' }); }
                } catch (e) { setError(e.message); } finally { setBusy(false); }
              }}>
              {t('pm.equip.create')}
            </Button>
            {created && (
              <div className="space-y-1 text-sm">
                <Row label={t('pm.equip.createdAs')} value={created.equipmentId} />
                {!created.tecoReady && (
                  <p className={isDarkMode ? 'text-amber-300 text-xs' : 'text-amber-700 text-xs'}>
                    {t('pm.equip.noCostCenterWarn')}
                  </p>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      <Card>
        <p className={`flex items-center gap-2 font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          <Gauge size={18} /> {t('pm.equip.readingsTitle')}
        </p>
        <div className="flex gap-2 mb-2">
          <input className={input(isDarkMode)} placeholder={t('pm.equip.searchPlaceholder')} value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') findEquipment(); }} />
          <Button variant="secondary" className="!min-h-[44px] !w-auto px-4" onClick={findEquipment}><Search size={18} /></Button>
        </div>
        {hits && hits.map((h) => (
          <button key={h.equipmentId} type="button" className="w-full text-left"
            onClick={() => openPoints(h.equipmentId)}>
            <p className={`text-sm py-1 underline ${sub}`}>{h.equipmentId} — {h.description}</p>
          </button>
        ))}
        {points && (
          points.items === null ? <p className={`text-sm ${sub}`}>{t('pm.common.loading')}</p>
          : points.items.length === 0 ? <p className={`text-sm ${sub}`}>{t('pm.equip.noPoints')}</p>
          : (
            <div className="mt-2 space-y-2">
              <select className={input(isDarkMode)} value={reading.point}
                onChange={(e) => setReading({ ...reading, point: e.target.value })}>
                {points.items.map((p) => (
                  <option key={p.point} value={p.point}>
                    {p.point} — {p.description}{p.unit ? ` (${p.unit})` : ''}{p.isCounter ? ` · ${t('pm.equip.counter')}` : ''}
                  </option>
                ))}
              </select>
              <input className={input(isDarkMode)} placeholder={t('pm.equip.readingValue')} value={reading.value}
                onChange={(e) => setReading({ ...reading, value: e.target.value })} />
              <Button variant="primary" className="!min-h-[48px]" disabled={busy || !reading.value}
                onClick={async () => {
                  setBusy(true); setError(null); setReadingResult(null);
                  try {
                    const res = await createMeasurementDoc(user, { point: reading.point, value: reading.value }, crypto.randomUUID(), role);
                    if (res && res.ok === false) setError(res.error || t('pm.equip.readingFailed'));
                    else { setReadingResult(res); setReading((r) => ({ ...r, value: '' })); }
                  } catch (e) { setError(e.message); } finally { setBusy(false); }
                }}>
                {t('pm.equip.recordReading')}
              </Button>
              {readingResult && (
                <p className={`text-sm ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                  {t('pm.equip.readingSaved', { id: readingResult.measurementDocId })}
                </p>
              )}
            </div>
          )
        )}
      </Card>
    </div>
  );
}
